import type { WorkflowDefinition } from "../types";

/**
 * W25 — Check a draft before it goes live.
 *
 * W13 for a content team rather than a comms team: the same three-step loop on
 * the same `draft` pipeline run, without the wire-clearance step at the end.
 *
 * `perTarget` for the same reason W13 is: a content calendar has several
 * drafts in flight at once. Every `since` is `scope: "target"`, so a
 * colleague's draft never checks off this run's steps.
 */
export const draftCheckWorkflow: WorkflowDefinition = {
	id: "draft-check",
	wNumber: 25,
	title: "Check a draft before you publish it",
	outcome:
		"Catch a citation problem while it still costs a revision, not a recrawl.",
	audiences: ["content", "inhouse", "pr"],
	uses: ["Citation Optimizer"],
	scope: "property",
	concurrency: "perTarget",
	progress: "explicit",
	target: { kind: "pipelineRun", label: "Draft", pipelineMode: "draft" },
	// Mirrors the Citation Optimizer route itself, which needs a content anchor
	// to score against: a BRAND's crawlable domain or a PRODUCT's seed URL.
	// PERSON, CATEGORY and LOCATION have neither, and that page 404s for them.
	isApplicable: (mode) => mode.isBrand || mode.isProduct,
	articleSlug: "how-to-check-a-draft-for-ai-citation-before-publishing",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "paste-draft",
			title: "Paste the draft while it is still a draft",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've scored a draft for this workflow.",
			why: "Nothing has to be live yet. Raw text from your editor scores the same way a published page does.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Paste the draft straight from your editor or CMS",
				"Set the page type: blog post, landing page or guide",
				"Clear any overlapping-pages warning before you go on",
			],
			complete: {
				kind: "all",
				of: [
					{
						kind: "since",
						event: "pipelineRunCreated",
						scope: "target",
					},
					{ kind: "count", key: "pipeline.draft", gte: 1 },
				],
			},
		},
		{
			id: "score",
			title: "Score it against the search it should win",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've completed a score for this workflow.",
			why: "Name the search first, then score. A draft written around a broad topic has nothing to be measured against.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Pick a tracked search rather than a phrase you hope matters",
				"Read every check, not the single number",
				"Score early drafts too, because it is free",
			],
			complete: {
				kind: "since",
				event: "pipelineRunCompleted",
				scope: "target",
			},
			signal: ({ state }) => {
				const score =
					state.target?.pipeline?.latestScore ??
					state.property?.pipeline.latestScore ??
					null;
				if (score === null) {
					return null;
				}
				return {
					kind: "score",
					label: "Citation score",
					value: score,
					target: 70,
				};
			},
		},
		{
			id: "revise",
			title: "Revise until the readiness banner clears",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've generated a rewrite for this workflow.",
			why: "Fix only the checks that failed. The banner is there so you do not have to guess when more editing stops paying.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Generate the rewrite and read the diff against the failing checks",
				"Score again for free after each round",
				"Export the copy, title, meta and structured data once it clears",
			],
			complete: {
				kind: "since",
				event: "pipelineRevisionCreated",
				scope: "target",
			},
			spend: { kind: "citationRewrite" },
			signal: ({ state }) => {
				const revisions = state.target?.pipeline?.revisionCount ?? null;
				if (revisions === null) {
					return null;
				}
				return {
					kind: "count",
					label: "Rewrites generated",
					value: revisions,
					target: 1,
				};
			},
		},
	],
};
